// ---------------------------------------------------------------------------
// useBrendaCharges
// ---------------------------------------------------------------------------
//
// The intended single source of truth for fetching product charges data.
//
// Responsibilities:
//   1. Accept a submit payload and fire a request to the prices API.
//   2. Orchestrate bank-holiday fetching/validation so the request doesn't
//      go out on a closed day.
//   3. Decide whether the response needs a re-fetch (date shift detection,
//      end-date bumped past a bank holiday, etc) and fire the follow-up
//      request transparently.
//   4. Run stock-checking for delivery and pickup against the final
//      response and produce a normalised, consumer-friendly result shape.
//
// Consumers never have to touch the raw Brenda response or repeat the
// availability checks — they read `result.delivery` / `result.pickup` /
// `result.product` and trust them.
// ---------------------------------------------------------------------------

// Global imports
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFetcher } from '@remix-run/react'

// App imports
import { useBankHolidays, useDebugLogger } from '~/hooks'
import { action as pricesAction } from '~/routes/api.get-prices'
import {
  buildBrendaChargesResult,
  evaluateBrendaCharges,
} from '~/utils/brendaCharges'
import { validateBrendaHireDates } from '~/utils/brendaDates'

// Types
import type {
  BrendaChargesDelivery,
  BrendaChargesErrorState,
  BrendaChargesPickup,
  BrendaChargesPickupPoint,
  BrendaChargesResult,
  BrendaChargesSubmit,
} from '~/types/brendaCharges'

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Entry point for the charges flow. Returns a submit function, the
 * current result, loading/error state and a couple of helpers.
 *
 * Usage:
 * ```ts
 * const { submitBrendaCharges, result, loading, error, clear } =
 *   useBrendaCharges()
 *
 * submitBrendaCharges({ stockCode, startDate, endDate, quantity, postcode })
 *
 * // …later, in render or an effect:
 * if (result) {
 *   if (result.delivery.available) { ... }
 *   if (result.pickup.available) { ... }
 * }
 * ```
 */
const useBrendaCharges = () => {
  // Debug logging — gated by the `DEBUG_CHECKOUT_FE` flag so production
  // consoles stay quiet.
  const debugRef = useDebugLogger('DEBUG_CHECKOUT_FE')

  // --------------------------------------------------------------------
  // Sub-hooks and fetchers
  // --------------------------------------------------------------------

  // Charges fetcher — hits the `api.get-prices` route.
  const fetcherCharges = useFetcher<typeof pricesAction>()
  const {
    submit: chargesSubmit,
    data: chargesData,
    state: chargesState,
  } = fetcherCharges

  // Bank-holiday integration. Uses the underlying hook's native state
  // names (`bankHolsData`, `bankHolsState`, `parseBankHolsResponse`).
  const {
    bankHolidays,
    bankHolsData,
    bankHolsState,
    setBankHolidays,
    ensureBankHolidays,
    parseBankHolsResponse,
  } = useBankHolidays()

  // --------------------------------------------------------------------
  // Internal refs — kept off React state so the response effect can read
  // up-to-date values without re-running every time one of them changes.
  // --------------------------------------------------------------------

  /** True when a request has been fired and we're waiting on a response. */
  const chargesRequestedRef = useRef(false)
  /** Holds a submit payload stashed while bank holidays are loading. */
  const pendingRequestRef = useRef<BrendaChargesSubmit | null>(null)
  /** Monotonic id used so each new error is a fresh state update. */
  const errorIdRef = useRef(0)
  /** The last request we actually sent to the API (post any corrections). */
  const lastRequestRef = useRef<BrendaChargesSubmit | null>(null)
  /**
   * The very first `startDate` / `endDate` submitted in the current
   * `useBrendaCharges` session. Set on the first fresh submit, and
   * preserved across every subsequent submit (internal re-fetches,
   * fulfilment-switch re-submits, quantity changes) until the result
   * is explicitly cleared via `clear()` or `failFlow()`. Consumers
   * read these to recompute the user's original duration when
   * switching fulfilment, so the new side's window always anchors on
   * the original range rather than any BH-bumped resolved end date.
   */
  const originalStartDateRef = useRef<string | null>(null)
  const originalEndDateRef = useRef<string | null>(null)
  /** True when the next-in-flight request is a re-fetch (skip shift detection). */
  const isRefetchRef = useRef(false)
  /**
   * When set, the next final result will be merged with the preserved
   * side from `prevResultRef` so the user can freely switch fulfilment
   * without losing the other side's original resolved dates.
   */
  const preserveSideRef = useRef<'delivery' | 'pickup' | null>(null)
  /** Snapshot of the previous result held across a fulfilment-switch re-fetch. */
  const prevResultRef = useRef<BrendaChargesResult | null>(null)
  /**
   * When an internal correction re-fetch fires for a *selected-side*
   * shift (e.g. pickup needs to move from 24th → 28th while delivery
   * is still fine at 24th), we stash the first response's non-selected
   * side here so the re-fetch — which anchors on the selected side's
   * new date — doesn't erase the other side's earlier availability.
   * Merged back into the final evaluation before composition. Only
   * preserved when the non-selected side was available on the first
   * response; otherwise we let the re-fetch's data stand so newly-
   * available dates still surface.
   */
  const correctionPreservedRef = useRef<{
    side: 'delivery' | 'pickup'
    delivery: BrendaChargesDelivery
    pickup: BrendaChargesPickup
  } | null>(null)
  /**
   * When set, the final result keeps the *branch list* and the modal
   * anchor (`submittedStart/End`) from the previous result instead of
   * the re-fetch's. Used by the pickup-branch select re-fetch: picking
   * a branch re-prices `pickup` for that branch's window, but the modal
   * should still list every branch against the user's requested window
   * — not collapse them all onto the chosen branch's later date.
   */
  const pickupPreserveRef = useRef<{
    pickupPoints: BrendaChargesPickupPoint[]
    submittedStartDate: string
    submittedEndDate: string
  } | null>(null)
  /**
   * True once the fetcher has actually transitioned out of `'idle'` for
   * the current request. Without this, the response effect can see
   * `state === 'idle'` on the render immediately after `chargesSubmit()`
   * — before the fetcher has started — and falsely flag "no data".
   */
  const inFlightRef = useRef(false)
  /**
   * Latest cached bank holidays, kept on a ref so `validateAndFire` and
   * the response effect can read the current value without re-binding.
   * Without this, `submitBrendaCharges` would change identity whenever
   * bank holidays load, forcing consumer memoisation to invalidate.
   */
  const bankHolidaysRef = useRef(bankHolidays)

  // Keep the ref in sync with the latest bank-holidays snapshot.
  useEffect(() => {
    bankHolidaysRef.current = bankHolidays

    debugRef.current('[useBrendaCharges] bank holidays cached:', {
      count: bankHolidays?.length ?? 0,
      holidays: bankHolidays,
    })
  }, [bankHolidays, debugRef])

  // --------------------------------------------------------------------
  // UI state
  // --------------------------------------------------------------------

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<BrendaChargesResult | null>(null)
  const [error, setError] = useState<BrendaChargesErrorState | null>(null)

  // --------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------

  /** Clears the current result — used on modal close, line removal, etc. */
  const clear = useCallback(() => {
    setResult(null)
    setError(null)
    originalStartDateRef.current = null
    originalEndDateRef.current = null
    preserveSideRef.current = null
    prevResultRef.current = null
    correctionPreservedRef.current = null
    pickupPreserveRef.current = null
  }, [])

  /** Emits an error with a fresh id so subscribers treat it as distinct. */
  const emitError = useCallback((message: string) => {
    errorIdRef.current += 1
    setError({
      id: errorIdRef.current,
      message,
      source: 'brendaCharges',
    })
  }, [])

  /** Tears down an in-flight request and surfaces an error message. */
  const failFlow = useCallback(
    (message?: string) => {
      debugRef.current('[useBrendaCharges] failFlow:', message)
      setLoading(false)
      setResult(null)
      chargesRequestedRef.current = false
      pendingRequestRef.current = null
      inFlightRef.current = false
      isRefetchRef.current = false
      originalStartDateRef.current = null
      originalEndDateRef.current = null
      lastRequestRef.current = null
      preserveSideRef.current = null
      prevResultRef.current = null
      correctionPreservedRef.current = null
      pickupPreserveRef.current = null
      emitError(
        message ?? "We couldn't fetch charges right now. Please try again."
      )
    },
    [emitError]
  )

  // --------------------------------------------------------------------
  // Fetcher wrapper — accepts a BrendaChargesSubmit, serialises it, and
  // records what we actually sent for the response effect.
  // --------------------------------------------------------------------

  const fireCharges = useCallback(
    (request: BrendaChargesSubmit) => {
      debugRef.current('[useBrendaCharges] fireCharges submit:', {
        startDate: request.startDate,
        endDate: request.endDate,
        quantity: request.quantity,
        isRefetch: isRefetchRef.current,
      })

      lastRequestRef.current = request

      chargesSubmit(
        {
          stockCode: request.stockCode,
          startDate: request.startDate,
          endDate: request.endDate,
          quantity: request.quantity,
          postcode: request.postcode,
          address: request.address ? JSON.stringify(request.address) : '',
          skipAddressSave: request.skipAddressSave ? 'true' : '',
        },
        {
          method: 'post',
          encType: 'application/json',
          action: '/api/get-prices',
        }
      )
    },
    [chargesSubmit]
  )

  // --------------------------------------------------------------------
  // Date validation + initial submit. Runs bank-holiday rules against
  // the submitted dates, bumps the end forward if needed, then fires.
  // --------------------------------------------------------------------

  const validateAndFire = useCallback(
    (submit: BrendaChargesSubmit) => {
      const result = validateBrendaHireDates(submit, bankHolidaysRef.current)

      if (!result.ok) {
        debugRef.current(
          '[useBrendaCharges] date validation failed:',
          result.message
        )
        failFlow(result.message)
        return
      }

      const finalRequest: BrendaChargesSubmit =
        result.adjustedEndDate === submit.endDate
          ? submit
          : { ...submit, endDate: result.adjustedEndDate }

      chargesRequestedRef.current = true
      setError(null)
      fireCharges(finalRequest)
    },
    [failFlow, fireCharges]
  )

  // --------------------------------------------------------------------
  // Public submit — gates on postcode presence, then either fires
  // straight away or stashes the request until bank holidays arrive.
  // --------------------------------------------------------------------

  const submitBrendaCharges = useCallback(
    (
      submit: BrendaChargesSubmit,
      options?: {
        preserveSide?: 'delivery' | 'pickup'
        /**
         * Keep the current result on screen during the re-fetch (spinner
         * overlay) instead of clearing it to null (full reload). Unlike
         * `preserveSide` this does NOT merge a side from the previous
         * result — the fresh response stands in full. Used by the
         * fulfilment switch so toggling sides never blanks the section.
         */
        keepResult?: boolean
        /**
         * Keep the previous result's branch list + modal anchor
         * (`submittedStart/End`) on the final result instead of the
         * re-fetch's. Used by the pickup-branch select re-fetch so the
         * modal keeps listing every branch against the requested window
         * while `pickup` itself re-prices for the chosen branch.
         */
        preservePickupPoints?: boolean
      }
    ) => {
      debugRef.current('[useBrendaCharges] submitBrendaCharges called:', {
        stockCode: submit.stockCode,
        startDate: submit.startDate,
        endDate: submit.endDate,
        preserveSide: options?.preserveSide,
      })

      if (!submit.postcode) {
        debugRef.current('[useBrendaCharges] submit: postcode missing')
        failFlow('Postcode missing')
        return
      }

      setLoading(true)

      // Fulfilment-switch re-fetches keep the previous result on screen
      // so the user's current selection doesn't flicker to null; every
      // other submit wipes the result. `keepResult` keeps it visible
      // without the preserve-side merge (fresh response stands in full).
      if (options?.preserveSide) {
        preserveSideRef.current = options.preserveSide
        prevResultRef.current = result
      } else {
        preserveSideRef.current = null
        prevResultRef.current = null
        if (!options?.keepResult) setResult(null)
      }

      // Stash the current branch list + modal anchor when asked to
      // preserve them across this re-fetch; otherwise drop any stale
      // stash so a normal submit surfaces the fresh branches.
      if (options?.preservePickupPoints && result) {
        pickupPreserveRef.current = {
          pickupPoints: result.product.pickupPoints,
          submittedStartDate: result.request.submittedStartDate,
          submittedEndDate: result.request.submittedEndDate,
        }
      } else {
        pickupPreserveRef.current = null
      }

      // Seed the "original" dates only when nothing is captured yet —
      // the very first submit of a session wins, and every subsequent
      // submit (re-fetch, fulfilment switch, quantity change) leaves
      // them untouched until `clear()` / `failFlow()` resets them.
      if (originalStartDateRef.current === null) {
        originalStartDateRef.current = submit.startDate
        originalEndDateRef.current = submit.endDate
      }

      // Reset in-flight state so a new submit always starts from a
      // clean slate, even if the previous one errored out mid-flow.
      chargesRequestedRef.current = false
      isRefetchRef.current = false
      inFlightRef.current = false

      const cacheStatus = ensureBankHolidays(submit.postcode)

      debugRef.current(
        '[useBrendaCharges] bank holidays cache status:',
        cacheStatus
      )

      if (cacheStatus === 'fetching') {
        debugRef.current(
          '[useBrendaCharges] stashing request while bank holidays load'
        )
        pendingRequestRef.current = submit
        return
      }

      // Already cached — straight into validation + fire
      validateAndFire(submit)
    },
    [ensureBankHolidays, failFlow, validateAndFire, result]
  )

  // --------------------------------------------------------------------
  // Bank-holidays response handler — resumes any pending request once
  // the holidays fetch settles (cached or idle with no data).
  // --------------------------------------------------------------------

  useEffect(() => {
    const pending = pendingRequestRef.current

    if (!pending) return

    // BH fetch ended with no data — continue without validation so the
    // user isn't left hanging forever.
    if (bankHolsState === 'idle' && !bankHolsData) {
      debugRef.current(
        '[useBrendaCharges] bank holidays fetch ended with no data; continuing'
      )

      setBankHolidays([])
      pendingRequestRef.current = null
      chargesRequestedRef.current = true
      setError(null)
      fireCharges(pending)
      return
    }

    if (bankHolsState !== 'idle' || !bankHolsData) return

    const holidays = parseBankHolsResponse()

    // BH API failed — cache empty and continue.
    if (holidays === null) {
      debugRef.current('[useBrendaCharges] bank holidays failed; continuing')
      setBankHolidays([])
      pendingRequestRef.current = null
      chargesRequestedRef.current = true
      setError(null)
      fireCharges(pending)
      return
    }

    // Sync the ref directly so the immediate `validateAndFire` below
    // sees the freshly-fetched list. The sync `useEffect` that keeps
    // `bankHolidaysRef` aligned with state doesn't fire until *after*
    // this render, so without this write the validator would run
    // against the stale (usually empty) ref and let a BH start date
    // slip through to Brenda.
    setBankHolidays(holidays)
    bankHolidaysRef.current = holidays
    pendingRequestRef.current = null
    validateAndFire(pending)
  }, [
    bankHolsState,
    bankHolsData,
    parseBankHolsResponse,
    setBankHolidays,
    fireCharges,
    validateAndFire,
  ])

  // --------------------------------------------------------------------
  // Charges response handler — the heart of the new flow. Runs stock
  // checking, decides if a re-fetch is needed, and (on the final
  // response) composes the consumer-friendly result shape.
  // --------------------------------------------------------------------

  useEffect(() => {
    // Track fetcher state transitions so the "no data" guard below can't
    // fire on the render immediately after submit.
    if (chargesState !== 'idle') {
      inFlightRef.current = true
      return
    }

    // Fetch ended with no data at all — genuine failure.
    if (chargesRequestedRef.current && inFlightRef.current && !chargesData) {
      debugRef.current('[useBrendaCharges] fetch ended with no data')
      inFlightRef.current = false
      failFlow()
      return
    }

    if (!chargesData) return

    inFlightRef.current = false
    chargesRequestedRef.current = false

    const { charges, startDate, endDate } = chargesData

    if (!charges || !startDate || !endDate) {
      setLoading(false)
      failFlow(
        'Pricing data unavailable, please try changing your dates or try fetching again'
      )
      return
    }

    debugRef.current('[useBrendaCharges] charges payload:', charges)

    // ----------------------------------------------------------------
    // Single-pass evaluation — resolves delivery + pickup, picks the
    // earliest-available fulfilment, and decides whether the submitted
    // dates need shifting. The hook re-fetches on `correction` (first
    // response only) and hands the whole evaluation off to
    // `buildBrendaChargesResult` on the final response.
    // ----------------------------------------------------------------

    // We should always have a request on the ref at this point because
    // `fireCharges` sets it before the fetcher is triggered. If it's
    // somehow missing we've hit an internal bug — surface it rather
    // than composing a result with empty stockCode/postcode fields.
    if (!lastRequestRef.current) {
      failFlow('Internal error — no active request when final response arrived')
      return
    }

    // Anchor the evaluator's correction maths on the user's original
    // requested range so duration is preserved across every re-fetch.
    const originalStartDate =
      originalStartDateRef.current ?? lastRequestRef.current.startDate
    const originalEndDate =
      originalEndDateRef.current ?? lastRequestRef.current.endDate

    const evaluation = evaluateBrendaCharges(
      charges,
      lastRequestRef.current,
      bankHolidaysRef.current,
      originalStartDate,
      originalEndDate
    )

    // `chosenSource` on the evaluation is the *earliest-available*
    // side, not the caller-selected one — surface both alongside each
    // other in the log so it's obvious which is which when reading
    // output from a flow where the user picked the later side.
    debugRef.current('[useBrendaCharges] evaluation:', {
      earliestSource: evaluation.chosenSource,
      earliestDeliveryDate: evaluation.delivery.date,
      earliestPickupDate: evaluation.pickup.date,
      selectedFulfilment: lastRequestRef.current?.selectedFulfilment ?? null,
      correction: evaluation.correction,
      raw: evaluation,
    })

    if (!isRefetchRef.current && evaluation.correction) {
      debugRef.current('[useBrendaCharges] dates shifted — re-fetching:', {
        submitted: { start: startDate, end: endDate },
        correction: evaluation.correction,
      })

      // If the correction is firing for a selected-side shift, stash
      // the first response's non-selected side so its (earlier)
      // availability survives the re-fetch. Only stash when that
      // side was actually available — if it wasn't, the re-fetch
      // might surface a newly-available date for it and we want to
      // see that.
      const selectedFulfilment = lastRequestRef.current.selectedFulfilment

      if (selectedFulfilment) {
        const otherSide =
          selectedFulfilment === 'pickup' ? 'delivery' : 'pickup'
        const otherSideAvailable =
          otherSide === 'delivery'
            ? evaluation.delivery.available
            : evaluation.pickup.available

        if (otherSideAvailable) {
          correctionPreservedRef.current = {
            side: otherSide,
            delivery: evaluation.delivery,
            pickup: evaluation.pickup,
          }
        } else {
          correctionPreservedRef.current = null
        }
      } else {
        correctionPreservedRef.current = null
      }

      isRefetchRef.current = true

      const nextRequest: BrendaChargesSubmit = {
        ...lastRequestRef.current,
        startDate: evaluation.correction.startDate,
        endDate: evaluation.correction.endDate,
      }

      // Route the re-fetch through validateAndFire so the corrected
      // start date still goes through the bank-holiday check — if the
      // evaluator picked a date that lands on a closed day we want to
      // surface an error, not silently submit it.
      validateAndFire(nextRequest)
      return
    }

    // ----------------------------------------------------------------
    // Final response — compose the result shape and expose it.
    // ----------------------------------------------------------------

    debugRef.current(
      '[useBrendaCharges] no correction — processing final response'
    )

    // If a correction re-fetch preserved the non-selected side from
    // the first response, overlay it onto the final evaluation so
    // `buildBrendaChargesResult` composes the result with both sides'
    // real availability — selected side from the re-fetch (accurate
    // pricing), other side from the first response (its earlier
    // window). `chosenSource` is recomputed from the merged dates so
    // the `earliest*` fields on the result reflect the true earliest.
    let mergedEvaluation = evaluation

    if (correctionPreservedRef.current) {
      const preserved = correctionPreservedRef.current
      const mergedDelivery = preserved.side === 'delivery'
        ? preserved.delivery
        : evaluation.delivery
      const mergedPickup = preserved.side === 'pickup'
        ? preserved.pickup
        : evaluation.pickup

      // Re-pick `chosenSource` across the merged sides. Mirrors the
      // tie-break in `evaluateBrendaCharges` — pickup wins ties for
      // bulk; for buy pickup must be strictly earlier.
      let mergedChosen: 'delivery' | 'pickup' | null = null

      if (
        mergedDelivery.available &&
        mergedPickup.available &&
        mergedDelivery.date &&
        mergedPickup.date
      ) {
        // We can't re-derive product type cheaply here, so use the
        // loose pickup-wins-ties rule — bulk is by far the common
        // case and consumables rarely have pickup available.
        mergedChosen =
          mergedPickup.date <= mergedDelivery.date ? 'pickup' : 'delivery'
      } else if (mergedDelivery.available) {
        mergedChosen = 'delivery'
      } else if (mergedPickup.available) {
        mergedChosen = 'pickup'
      }

      mergedEvaluation = {
        ...evaluation,
        delivery: mergedDelivery,
        pickup: mergedPickup,
        chosenSource: mergedChosen,
      }

      debugRef.current(
        '[useBrendaCharges] merging preserved non-selected side:',
        {
          preservedSide: preserved.side,
          mergedDeliveryDate: mergedDelivery.date,
          mergedPickupDate: mergedPickup.date,
          mergedChosen,
        }
      )
    }

    correctionPreservedRef.current = null

    const composed = buildBrendaChargesResult(
      charges,
      lastRequestRef.current,
      mergedEvaluation,
      originalStartDate,
      originalEndDate
    )

    // Summary first, then the full dump. `resolved*` tracks the side
    // the caller committed to (via `selectedFulfilment`) — and falls
    // back to earliest when no side was selected. `earliest*` always
    // tracks the earliest-available side regardless.
    debugRef.current('[useBrendaCharges] composed result summary:', {
      selectedFulfilment: composed.request.selectedFulfilment,
      resolvedStartDate: composed.request.resolvedStartDate,
      resolvedEndDate: composed.request.resolvedEndDate,
      earliestFulfilment: composed.request.earliestFulfilment,
      earliestStartDate: composed.request.earliestStartDate,
      earliestEndDate: composed.request.earliestEndDate,
    })

    debugRef.current('[useBrendaCharges] composed result:', composed)

    // Fulfilment-switch re-fetch: keep the preserved side's data from
    // the previous result so the user can freely switch back without
    // losing its original resolved dates / availability.
    let finalResult = composed

    if (preserveSideRef.current && prevResultRef.current) {
      const preservedSide = preserveSideRef.current
      const prev = prevResultRef.current
      debugRef.current(
        '[useBrendaCharges] preserving side from previous result:',
        preservedSide
      )
      finalResult =
        preservedSide === 'delivery'
          ? { ...composed, delivery: prev.delivery }
          : { ...composed, pickup: prev.pickup }
    }

    // Pickup-branch select re-fetch: keep the previous branch list +
    // modal anchor so the modal still lists every branch against the
    // requested window, while `pickup` reflects the re-priced chosen
    // branch.
    if (pickupPreserveRef.current) {
      const preserved = pickupPreserveRef.current
      debugRef.current(
        '[useBrendaCharges] preserving pickup branch list + anchor'
      )
      finalResult = {
        ...finalResult,
        product: {
          ...finalResult.product,
          pickupPoints: preserved.pickupPoints,
        },
        request: {
          ...finalResult.request,
          submittedStartDate: preserved.submittedStartDate,
          submittedEndDate: preserved.submittedEndDate,
        },
      }
    }

    isRefetchRef.current = false
    preserveSideRef.current = null
    prevResultRef.current = null
    pickupPreserveRef.current = null
    lastRequestRef.current = null
    setLoading(false)
    setError(null)
    setResult(finalResult)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargesState, chargesData])

  // --------------------------------------------------------------------
  // Hook API
  // --------------------------------------------------------------------

  return {
    /** Fire a charges request. Handles BH integration + re-fetch internally. */
    submitBrendaCharges,
    /** The most recently resolved result, or null before any submit / after clear. */
    result,
    /** True while a submit is in flight (including any internal re-fetch). */
    loading,
    /** Most recent error, or null. Each error has a fresh `id` so subscribers can react. */
    error,
    /** Clears the current result + error. */
    clear,
    /** Bank holidays cached for the active postcode — exposed for UIs that need them. */
    bankHolidays,
    /**
     * Pre-warm the bank-holidays cache for a postcode ahead of any
     * charges submit. Consumers that know the postcode early (e.g. the
     * PDP once an address is entered) can call this so date pickers
     * can flag / block BH dates before the user tries to fetch prices,
     * and so the first submit doesn't pay the BH fetch latency.
     * Returns `'cached'` when already available, `'fetching'` when a
     * fetch was triggered.
     */
    ensureBankHolidays,
  }
}

export default useBrendaCharges
